import chalk from "chalk";

type LogLevel = "debug" | "info" | "warn" | "error";
type LogMethod = LogLevel | "log";
type RuntimeEnvironment = "server" | "client";
type Mode = "always" | "dev-only" | "prod-only" | "never";

interface EnvironmentConfig {
  mode?: Mode;
  minLevel?: LogLevel;
}

type Colorizer = (value: string) => string;

interface PrefixColors {
  level: Colorizer;
  env: Colorizer;
  namespace: Colorizer;
  muted: Colorizer;
}

interface PrefixContext {
  level: LogMethod;
  env: RuntimeEnvironment;
  namespace?: string;
  isDev: boolean;
  colors: PrefixColors;
}

interface LoggerConfig {
  enabled?: boolean;
  prefix?: string | ((context: PrefixContext) => string);
  includeTimestamp?: boolean;
  server?: EnvironmentConfig;
  client?: EnvironmentConfig;
}

type WriteArgs = unknown[];

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const DEFAULT_COLORS: PrefixColors = {
  level: (value) => value,
  env: (value) => value,
  namespace: (value) => value,
  muted: (value) => value,
};

const DEFAULT_CONFIG: Required<LoggerConfig> = {
  enabled: true,
  includeTimestamp: false,
  prefix: ({ level, env, namespace, isDev, colors }) => {
    const levelLabel = colors.level(level.toUpperCase());
    const envLabel = colors.env(env === "server" ? "SRV" : "CLT");
    const buildLabel = colors.muted(isDev ? "DEV" : "PROD");
    const nsLabel = namespace ? colors.namespace(namespace) : null;
    const nsSection = nsLabel ? `[${nsLabel}]` : "";
    return `[${buildLabel}]${nsSection}[${envLabel}][${levelLabel}]`;
  },
  server: { mode: "always", minLevel: "debug" },
  client: { mode: "dev-only", minLevel: "info" },
};

const MODULE_PATH_HINT = (() => {
  try {
    return new URL(import.meta.url).pathname ?? "";
  } catch {
    return "";
  }
})();
const STACK_OMIT_HINTS = [MODULE_PATH_HINT, "src/lib/logger.ts", "logger.ts"];

function mergeConfig(
  base: Required<LoggerConfig>,
  overrides: LoggerConfig = {},
): Required<LoggerConfig> {
  return {
    enabled: overrides.enabled ?? base.enabled,
    includeTimestamp: overrides.includeTimestamp ?? base.includeTimestamp,
    prefix: overrides.prefix ?? base.prefix,
    server: { ...base.server, ...overrides.server },
    client: { ...base.client, ...overrides.client },
  };
}

interface SourceInfo {
  name?: string;
  filePath?: string;
  line?: string;
  column?: string;
}

function getSource(): SourceInfo | null {
  const regex = /at\s+(\w+).*?(src\/.*?):(\d+):(\d+)/;
  let source: string | null = null;

  try {
    throw new Error();
  } catch (error) {
    source =
      (error as Error).stack
        ?.split("\n")
        .slice(1)
        .find((line) => line && STACK_OMIT_HINTS.every((hint) => !line.includes(hint))) || null;
  }

  const match = source?.match(regex);
  const name = match?.[1] || null;
  const filePath = match?.[2] || null;
  const line = match?.[3] || null;
  const column = match?.[4] || null;

  if (!name && !filePath) {
    return null;
  }

  return {
    name: name ?? undefined,
    filePath: filePath ?? undefined,
    line: line ?? undefined,
    column: column ?? undefined,
  };
}

function getConsoleWriter(method: LogMethod) {
  switch (method) {
    case "debug":
      return console.debug ?? console.log;
    case "info":
    case "log":
      return console.info ?? console.log;
    case "warn":
      return console.warn ?? console.log;
    case "error":
      return console.error ?? console.log;
    default:
      return console.log;
  }
}

function normalizeLevel(method: LogMethod): LogLevel {
  return method === "log" ? "info" : method;
}

function shouldOutput(mode: Mode, isDev: boolean) {
  if (mode === "always") return true;
  if (mode === "dev-only") return isDev;
  if (mode === "prod-only") return !isDev;
  return false;
}

function getColors(level: LogLevel): PrefixColors {
  if (!chalk) {
    return DEFAULT_COLORS;
  }
  return {
    level: (value: string) => {
      switch (level) {
        case "debug":
          return chalk.gray(value);
        case "info":
          return chalk.cyan(value);
        case "warn":
          return chalk.yellow(value);
        case "error":
          return chalk.red(value);
        default:
          return value;
      }
    },
    env: (value: string) => chalk.blue(value),
    namespace: (value: string) => chalk.magenta(value),
    muted: (value: string) => chalk.gray(value),
  };
}

class Logger {
  private config: Required<LoggerConfig> = DEFAULT_CONFIG;
  private readonly isDev = process.env.NODE_ENV !== "production";
  private readonly runtime: RuntimeEnvironment =
    typeof window === "undefined" ? "server" : "client";

  configure(overrides: LoggerConfig = {}) {
    this.config = mergeConfig(this.config, overrides);
  }

  withNamespace(namespace: string) {
    return new ScopedLogger(this, namespace);
  }

  log(...args: WriteArgs) {
    this.write("log", args);
  }

  info(...args: WriteArgs) {
    this.write("info", args);
  }

  debug(...args: WriteArgs) {
    this.write("debug", args);
  }

  warn(...args: WriteArgs) {
    this.write("warn", args);
  }

  error(...args: WriteArgs) {
    this.write("error", args);
  }

  logWithNamespace(method: LogMethod, namespace: string, args: WriteArgs) {
    this.write(method, args, namespace);
  }

  private write(method: LogMethod, args: WriteArgs, namespace?: string) {
    if (!this.config.enabled) {
      return;
    }

    const level = normalizeLevel(method);
    const envConfig = this.runtime === "server" ? this.config.server : this.config.client;
    const outputMode = envConfig.mode ?? "always";

    if (!shouldOutput(outputMode, this.isDev)) {
      return;
    }

    const minLevelPriority = LEVEL_PRIORITY[envConfig.minLevel ?? "debug"];
    if (LEVEL_PRIORITY[level] < minLevelPriority) {
      return;
    }

    const colors = this.resolveColors(level);
    const prefix = this.resolvePrefix(method, namespace, colors);
    const timestamp = this.config.includeTimestamp ? new Date().toISOString() : undefined;

    this.output(method, prefix, timestamp, args, colors);
  }

  private resolveColors(level: LogLevel): PrefixColors {
    return getColors(level);
  }

  private resolvePrefix(method: LogMethod, namespace: string | undefined, colors: PrefixColors) {
    const { prefix } = this.config;
    if (typeof prefix === "function") {
      return prefix({
        level: method,
        env: this.runtime,
        namespace,
        isDev: this.isDev,
        colors,
      });
    }
    return prefix;
  }

  private formatMessageParts(prefix: string, timestamp: string | undefined, colors: PrefixColors) {
    const parts: string[] = [prefix];
    if (timestamp) {
      parts.push(colors.muted(timestamp));
    }

    return parts;
  }

  private output(
    method: LogMethod,
    prefix: string,
    timestamp: string | undefined,
    args: WriteArgs,
    colors: PrefixColors,
  ) {
    const writer = getConsoleWriter(method);
    const source = getSource();
    const lines = [, ...this.formatMessageParts(prefix, timestamp, colors)];

    if (source) {
      const pathPart = source.filePath ? chalk.underline(source.filePath) : chalk.dim("unknown");
      const position = source.line
        ? `:${source.line}${source.column ? `:${source.column}` : ""}`
        : "";
      const namePart = source.name ? `${chalk.bold(source.name)} ` : "";
      lines.push(`${namePart}(${pathPart}${position})`);
    }

    const message = lines.join("\n");

    if (args.length) {
      writer(message, ...args, "\n");
    } else {
      writer(message, "\n");
    }
  }
}

class ScopedLogger {
  constructor(
    private readonly root: Logger,
    private readonly namespace: string,
  ) {}

  configure(overrides: LoggerConfig = {}) {
    this.root.configure(overrides);
  }

  log(...args: WriteArgs) {
    this.root.logWithNamespace("log", this.namespace, args);
  }

  info(...args: WriteArgs) {
    this.root.logWithNamespace("info", this.namespace, args);
  }

  debug(...args: WriteArgs) {
    this.root.logWithNamespace("debug", this.namespace, args);
  }

  warn(...args: WriteArgs) {
    this.root.logWithNamespace("warn", this.namespace, args);
  }

  error(...args: WriteArgs) {
    this.root.logWithNamespace("error", this.namespace, args);
  }
}

const logger = new Logger();

export { logger };
export type { LoggerConfig, LogLevel, LogMethod, PrefixColors, PrefixContext, RuntimeEnvironment };

export const configureLogger = (overrides: LoggerConfig) => {
  logger.configure(overrides);
};

export const getLogger = (namespace: string) => logger.withNamespace(namespace);

const modeCandidates: Record<string, Mode> = {
  always: "always",
  "dev-only": "dev-only",
  "prod-only": "prod-only",
  never: "never",
};

const levelCandidates: Record<string, LogLevel> = {
  debug: "debug",
  info: "info",
  warn: "warn",
  warning: "warn",
  error: "error",
};

function parseMode(value: string | undefined): Mode | undefined {
  if (!value) return undefined;
  return modeCandidates[value.toLowerCase()];
}

function parseLevel(value: string | undefined): LogLevel | undefined {
  if (!value) return undefined;
  return levelCandidates[value.toLowerCase()];
}

const envOverrides: LoggerConfig = {};

const includeTimestampEnv =
  process.env.LOG_INCLUDE_TIMESTAMP ?? process.env.NEXT_PUBLIC_LOG_INCLUDE_TIMESTAMP;
if (typeof includeTimestampEnv !== "undefined") {
  envOverrides.includeTimestamp = includeTimestampEnv === "true";
}

const prefixTemplate =
  process.env.LOG_PREFIX_TEMPLATE ?? process.env.NEXT_PUBLIC_LOG_PREFIX_TEMPLATE;
if (prefixTemplate) {
  envOverrides.prefix = ({ level, env, namespace }) => {
    const replacements: Record<string, string> = {
      "{level}": level.toUpperCase(),
      "{env}": env,
      "{namespace}": namespace ?? "",
    };
    let formatted = prefixTemplate;
    Object.entries(replacements).forEach(([token, value]) => {
      formatted = formatted.replaceAll(token, value);
    });
    return formatted;
  };
}

const serverMode = parseMode(process.env.SERVER_LOG_MODE);
const serverLevel = parseLevel(process.env.SERVER_LOG_LEVEL ?? process.env.LOG_LEVEL);
if (serverMode || serverLevel) {
  envOverrides.server = {
    ...(envOverrides.server ?? {}),
    ...(serverMode ? { mode: serverMode } : {}),
    ...(serverLevel ? { minLevel: serverLevel } : {}),
  };
}

const clientMode = parseMode(process.env.NEXT_PUBLIC_CLIENT_LOG_MODE);
const clientLevel = parseLevel(process.env.NEXT_PUBLIC_CLIENT_LOG_LEVEL);
if (clientMode || clientLevel) {
  envOverrides.client = {
    ...(envOverrides.client ?? {}),
    ...(clientMode ? { mode: clientMode } : {}),
    ...(clientLevel ? { minLevel: clientLevel } : {}),
  };
}

if (
  envOverrides.prefix ||
  typeof envOverrides.includeTimestamp !== "undefined" ||
  envOverrides.server ||
  envOverrides.client
) {
  logger.configure(envOverrides);
}
